import imageCompression from 'browser-image-compression'
// The public CSP entry embeds a second decoder as worker-source text. Import
// only the pinned source detector; the build drops its unused conversion code.
import { isHeic } from '../node_modules/heic-to/src/index.js'

const TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' }
const HEIF_EXTENSION = /\.(heic|heif)$/i
const COMPRESSOR_URL = 'https://cdn.jsdelivr.net/npm/browser-image-compression@2.0.2/dist/browser-image-compression.js'

export class ImagePreparationError extends Error {
  constructor(file, cause) {
    super(`Could not prepare "${file.name}". Please choose a readable JPEG, PNG, or HEIC image and try again.`, { cause })
    this.name = 'ImagePreparationError'
    this.filename = file.name
  }
}

async function signature(blob) {
  const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer())
  if (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) return 'image/jpeg'
  if ([137, 80, 78, 71, 13, 10, 26, 10].every((b, i) => bytes[i] === b)) return 'image/png'
  return bytes.length >= 12 && String.fromCharCode(...bytes.subarray(4, 8)) === 'ftyp' ? 'heif-container' : ''
}

function uploadFile(blob, source, name, type) {
  if (blob === source && source.name === name && source.type === type) return source
  return new File([blob], name, { type, lastModified: source.lastModified })
}

/** Private form capability: prepare one File; never change the selected FileList.
 * Returns unchanged noneligible attachments. Rejects eligible decode/encode errors
 * with ImagePreparationError (filename, cause). Caller owns sequential scheduling.
 */
export async function prepareUploadImage(file) {
  const extension = file.name.split('.').pop().toLowerCase()
  const intendedType = TYPES[extension]
  try {
    const content = await signature(file)
    let converted = false
    let type = intendedType
    let name = file.name
    let input = file

    if (content === 'heif-container') {
      if (await isHeic(file)) {
        const { primaryHeicToJpeg } = await import('./primary-heic.js')
        input = await primaryHeicToJpeg(file)
        converted = true
        type = 'image/jpeg'
        name = file.name.replace(/\.[^.]*$/, '') + '.jpg'
      }
    }
    if (!converted) {
      if (HEIF_EXTENSION.test(name) && content === 'image/jpeg') {
        type = 'image/jpeg'
        name = name.replace(HEIF_EXTENSION, '.jpg')
      } else if (!intendedType && !HEIF_EXTENSION.test(name)) {
        return file // GIF/WebP/other attachments keep bytes and policy unchanged, including BMP.
      }
      if (!type || content !== type) throw new Error('Unsupported or mismatched image encoding')
    }

    if (input.type !== type) input = uploadFile(input, file, name, type)
    const output = await imageCompression(input, {
      maxSizeMB: 2,
      maxWidthOrHeight: 2560,
      fileType: type,
      preserveExif: false,
      useWebWorker: true,
      libURL: COMPRESSOR_URL,
    })
    if (output.type !== type || await signature(output) !== type) {
      throw new Error('Unexpected encoder output encoding')
    }
    // Compatible HEIC conversion takes precedence over byte savings. For JPEG/PNG
    // retaining the original also retains its metadata; this is not a sanitizer.
    const selected = output.size < input.size ? output : input
    return uploadFile(selected, file, name, type)
  } catch (cause) {
    throw new ImagePreparationError(file, cause)
  }
}
